/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/string/ReplaceAllStringBetween.ts
 * @create:      2025-06-26 13:13:35.193
 * @modify:      2025-07-11 21:19:12.831
 * @version:     6.0.0
 * @times:       10
 * @lines:       77
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { TReplaceFunction } from "../type/basic";
import { GetStringPositionBetween } from "./GetStringPositionBetween";
import { ReplaceAt } from "./ReplaceAt";

/**
 * Replaces all substrings found between the specified `start` and `end` delimiters in the input string,
 * using the provided `replacer` function to generate the replacement for each match.
 *
 * The function searches for all non-overlapping occurrences of substrings between `start` and `end` within `input`,
 * and replaces each occurrence by invoking `replacer` with the matched substring.
 *
 * @param replacer - A function that takes the matched substring and returns the replacement string.
 * @param input - The input string to process.
 * @param start - The starting delimiter string.
 * @param end - The ending delimiter string.
 * @returns The resulting string after all replacements have been made.
 * 
 * @since 1.1.7
 * @version 2021-12-07
 */
export function ReplaceAllStringBetween(replacer: TReplaceFunction<string>, input: string, start: string, end: string) {
    let current = 0;

    while (true) {
        const pos = GetStringPositionBetween(
            input,
            start,
            end,
            1,
            current,
        );
        if (pos.length > 0) {
            const p = pos[0];

            const replacedStr = ReplaceAt(input, p, replacer(p.RelatedString!));
            current = p.End! + 1 + (replacedStr.length - input.length);
            input = replacedStr;
        } else {
            break;
        }
    }

    return input;
}
